import { useEffect, useState } from 'react';
import { NavLink, Outlet, useLoaderData, useLocation, useNavigate } from 'react-router';
import {
  CheckCircle,
  Layers,
  Menu,
  Package,
  PlusSquare,
  Settings2,
  Tag,
  X,
} from 'lucide-react';
import { ToastViewport } from '../ui/Toast';
import { api } from '../../lib/api';
import { pushToast } from '../../lib/toast';

interface LoaderData {
  user: { id: string; email: string };
}

const navItems = [
  { to: '/orders', icon: Package, label: '주문' },
  { to: '/manual-order', icon: PlusSquare, label: '수동주문' },
  { to: '/done', icon: CheckCircle, label: '완료' },
  { to: '/batches', icon: Layers, label: '발주목록' },
  { to: '/lumi-code', icon: Tag, label: '상품코드' },
  { to: '/settings', icon: Settings2, label: '설정' },
];

export function AppShell() {
  const { user } = useLoaderData() as LoaderData;
  const nav = useNavigate();
  const location = useLocation();
  const [drawerOpen, setDrawerOpen] = useState(false);

  useEffect(() => {
    setDrawerOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (!drawerOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setDrawerOpen(false);
    };
    document.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prev;
    };
  }, [drawerOpen]);

  const sidebarLinkClass = ({ isActive }: { isActive: boolean }) =>
    [
      'group relative flex items-center gap-3 rounded-lg px-3 py-2',
      'text-[13px] font-medium leading-none',
      'transition-colors duration-150 ease-out',
      'justify-center xl:justify-start',
      isActive
        ? 'bg-ink-950 text-paper'
        : 'text-ink-700 hover:bg-ink-100 hover:text-ink-950',
      'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2 focus-visible:ring-offset-canvas',
    ].join(' ');

  const drawerLinkClass = ({ isActive }: { isActive: boolean }) =>
    [
      'flex items-center gap-3 rounded-lg px-3 py-3 text-[15px]',
      'transition-colors duration-150 ease-out',
      isActive
        ? 'bg-ink-950 text-paper font-medium'
        : 'text-ink-700 hover:bg-ink-100',
    ].join(' ');

  async function handleSignout() {
    try {
      await api.post('/auth/signout');
      nav('/signin');
    } catch {
      pushToast('error', '로그아웃에 실패했습니다. 다시 시도해 주세요.');
    }
  }

  return (
    <div className="flex h-screen flex-col bg-canvas">
      {/* AppBar */}
      <header className="z-20 flex h-14 shrink-0 items-center justify-between border-b border-ink-200 bg-paper/85 px-3 backdrop-blur sm:px-5 lg:px-7">
        <div className="flex items-center gap-3">
          <button
            type="button"
            aria-label="메뉴 열기"
            className="-ml-1 inline-flex h-9 w-9 items-center justify-center rounded-full text-ink-700 hover:bg-ink-100 lg:hidden"
            onClick={() => setDrawerOpen(true)}
          >
            <Menu size={18} aria-hidden="true" />
          </button>
          <NavLink
            to="/orders"
            className="group flex items-center gap-2 rounded-full focus:outline-none focus-visible:ring-2 focus-visible:ring-coral-500 focus-visible:ring-offset-2 focus-visible:ring-offset-paper"
          >
            <span aria-hidden="true" className="relative inline-flex h-2.5 w-2.5">
              <span className="absolute inset-0 rounded-full bg-ink-950" />
              <span className="absolute -right-0.5 -top-0.5 h-1.5 w-1.5 rounded-full bg-coral-500 shadow-[0_0_8px_rgba(255,107,74,0.7)]" />
            </span>
            <span className="text-[14px] font-semibold tracking-[-0.01em] text-ink-950">
              Auto-OMS
            </span>
          </NavLink>
          {import.meta.env.DEV && (
            <span className="rounded-full border border-ink-200 bg-paper px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.08em] text-ink-500">
              Dev
            </span>
          )}
        </div>
        <div className="flex items-center gap-3">
          <span
            className="hidden max-w-[40vw] truncate text-[12px] text-ink-500 sm:block"
            title={user.email}
          >
            {user.email}
          </span>
          <button className="btn-secondary btn-sm" onClick={handleSignout}>
            로그아웃
          </button>
        </div>
      </header>

      <div className="flex flex-1 overflow-hidden">
        {/* Desktop sidebar */}
        <nav
          aria-label="주요 메뉴"
          className="hidden shrink-0 flex-col border-r border-ink-200 bg-paper py-4 lg:flex lg:w-16 xl:w-60"
        >
          <ul className="flex flex-col gap-1 px-2">
            {navItems.map(({ to, icon: Icon, label }) => (
              <li key={to}>
                <NavLink to={to} className={sidebarLinkClass} aria-label={label}>
                  <Icon size={18} aria-hidden="true" strokeWidth={1.75} />
                  <span className="hidden xl:block">{label}</span>
                </NavLink>
              </li>
            ))}
          </ul>
          <div className="mt-auto hidden xl:block px-4 pb-2">
            <p className="label-overline">Auto-OMS</p>
            <p className="mt-1 text-[11px] text-ink-400">v1 · light theme</p>
          </div>
        </nav>

        {/* Mobile drawer */}
        {drawerOpen && (
          <div
            className="fixed inset-0 z-50 lg:hidden"
            role="dialog"
            aria-modal="true"
            aria-label="주요 메뉴"
          >
            <div
              className="absolute inset-0 bg-ink-950/40 backdrop-blur-[2px]"
              onClick={() => setDrawerOpen(false)}
            />
            <aside className="absolute inset-y-0 left-0 flex w-72 max-w-[85vw] flex-col bg-paper shadow-modal">
              <div className="flex h-14 shrink-0 items-center justify-between border-b border-ink-200 px-4">
                <span className="flex items-center gap-2">
                  <span aria-hidden="true" className="relative inline-flex h-2.5 w-2.5">
                    <span className="absolute inset-0 rounded-full bg-ink-950" />
                    <span className="absolute -right-0.5 -top-0.5 h-1.5 w-1.5 rounded-full bg-coral-500" />
                  </span>
                  <span className="text-[14px] font-semibold tracking-[-0.01em] text-ink-950">
                    Auto-OMS
                  </span>
                </span>
                <button
                  type="button"
                  aria-label="메뉴 닫기"
                  className="inline-flex h-9 w-9 items-center justify-center rounded-full text-ink-600 hover:bg-ink-100"
                  onClick={() => setDrawerOpen(false)}
                >
                  <X size={18} aria-hidden="true" />
                </button>
              </div>
              <ul className="flex flex-1 flex-col gap-1 overflow-y-auto p-3">
                {navItems.map(({ to, icon: Icon, label }) => (
                  <li key={to}>
                    <NavLink to={to} className={drawerLinkClass}>
                      <Icon size={18} aria-hidden="true" strokeWidth={1.75} />
                      <span>{label}</span>
                    </NavLink>
                  </li>
                ))}
              </ul>
              <div className="shrink-0 border-t border-ink-200 px-4 py-3">
                <p className="label-overline">로그인됨</p>
                <p className="mt-1 truncate text-[12px] text-ink-700" title={user.email}>
                  {user.email}
                </p>
              </div>
            </aside>
          </div>
        )}

        {/* Main content */}
        <main className="flex flex-1 flex-col overflow-auto">
          <div className="mx-auto w-full max-w-[1440px] px-4 py-6 sm:px-6 sm:py-7 lg:px-8 lg:py-8">
            <Outlet />
          </div>
        </main>
      </div>

      <ToastViewport />
    </div>
  );
}
